<template>
  <div class="landing-page">
    <!-- 装饰背景光斑 (Ambient Lighting) -->
    <div class="ambient-glow glow-top-left"></div>
    <div class="ambient-glow glow-top-right"></div>
    <div class="ambient-glow glow-middle"></div>

    <!-- 1. 现代化通透顶部导航栏 -->
    <header class="glass-navbar">
      <div class="nav-container">
        <!-- Logo 区域 -->
        <div class="brand-logo" @click="$router.push('/')">
          <div class="logo-badge">
            <!--            <el-icon><Compass /></el-icon>-->
            <img
              src="./assets/img/img.png"
              alt=""
              style="width: 100%; height: 100%"
            />
          </div>
          <div class="brand-text">
            <span class="main-title">BAGL</span>
            <span class="sub-title">Creator Studio</span>
          </div>
        </div>

        <!-- 导航锚点 -->
        <nav class="nav-links">
          <a class="nav-item" @click.prevent="scrollToSection('features')"
            >核心特性</a
          >
          <a class="nav-item" @click.prevent="scrollToSection('showcase')"
            >玩法矩阵</a
          >
          <a class="nav-item" @click.prevent="scrollToSection('app-download')"
            >移动端 App</a
          >
          <a class="nav-item" @click.prevent="openAbout">关于我们</a>
        </nav>

        <!-- 用户状态与语言操作区 -->
        <div class="nav-actions">
          <!-- 国际化语言切换 -->
          <el-dropdown
            trigger="click"
            @command="handleCommand"
            class="lang-dropdown"
          >
            <button class="icon-action-btn" title="切换语言">
              <el-icon><Document /></el-icon>
            </button>
            <template #dropdown>
              <el-dropdown-menu class="custom-dropdown-menu">
                <el-dropdown-item command="cn">简体中文</el-dropdown-item>
                <el-dropdown-item command="jp">繁体中文</el-dropdown-item>
                <el-dropdown-item command="en">English</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>

          <!-- 登录状态控制 -->
          <template v-if="isLogin">
            <el-button type="primary" class="create-btn" @click="CreateGame">
              <el-icon><Plus /></el-icon>
              <span>创作新游戏</span>
            </el-button>

            <el-dropdown trigger="click" class="user-dropdown">
              <div class="avatar-wrapper">
                <el-avatar
                  :src="userInfo?.avatar"
                  :size="38"
                  class="user-avatar"
                >
                  {{ userInfo?.name ? userInfo.name.charAt(0) : "U" }}
                </el-avatar>
                <span class="user-name">{{ userInfo?.name || "创作者" }}</span>
              </div>
              <template #dropdown>
                <el-dropdown-menu class="custom-dropdown-menu">
                  <el-dropdown-item @click="$router.push('/list')">
                    <el-icon><FolderOpened /></el-icon>
                    <span>{{ $t("home.list") || "游戏作品库" }}</span>
                  </el-dropdown-item>
                  <el-dropdown-item @click="$router.push('/setting')">
                    <el-icon><Setting /></el-icon>
                    <span>{{ $t("home.Setting") || "个人与系统设置" }}</span>
                  </el-dropdown-item>
                  <el-dropdown-item divided @click="logout" class="danger-item">
                    <el-icon><SwitchButton /></el-icon>
                    <span>{{ $t("home.logout") || "退出登录" }}</span>
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </template>

          <template v-else>
            <el-button class="login-text-btn" @click="$router.push('/login')">
              登录
            </el-button>
            <el-button
              type="primary"
              class="join-btn"
              @click="$router.push('/login')"
            >
              免费注册入驻
            </el-button>
          </template>
        </div>
      </div>
    </header>

    <!-- 2. Hero 首屏核心展示区 -->
    <section class="hero-section">
      <div class="hero-content">
        <div class="hero-pill-badge">
          <span class="pulse-dot"></span>
          <span>新一代现实增强实景探索创作引擎</span>
        </div>
        <h1 class="hero-title">
          将整座城市与自然景区<br />
          变成你笔下的<span class="gradient-text">沉浸式解谜游戏</span>
        </h1>
        <p class="hero-description">
          免写代码、可视化编排真实地理坐标、AR
          识别图像、分步剧情线索与多角色羁绊。面向文旅景区、城市探索团队与剧本创作者的专业级
          SaaS 工具。
        </p>

        <div class="hero-cta-group">
          <el-button
            type="primary"
            size="large"
            class="hero-primary-btn"
            @click="isLogin ? CreateGame() : $router.push('/login')"
          >
            <el-icon><VideoPlay /></el-icon>
            <span>{{ isLogin ? "进入创作者工作台" : "立即开始创作" }}</span>
          </el-button>
          <el-button
            size="large"
            class="hero-secondary-btn"
            @click="scrollToSection('features')"
          >
            <span>了解平台功能</span>
            <el-icon><ArrowDown /></el-icon>
          </el-button>
        </div>

        <!-- 合作/平台特性徽标横栏 -->
        <div class="partner-strip">
          <span class="strip-label">赋能多样化落地场景</span>
          <div class="partner-logos">
            <span class="partner-chip">城市漫游 (City Walk)</span>
            <span class="partner-chip">实景沉浸剧本游</span>
            <span class="partner-chip">景区研学互动导览</span>
            <span class="partner-chip">AR 寻宝打卡展演</span>
          </div>
        </div>
      </div>
    </section>

    <!-- 3. Bento Grid 核心特性展示区 (features) -->
    <section class="features-section" id="features">
      <div class="section-heading">
        <span class="subhead">CORE CAPABILITIES</span>
        <h2 class="head-title">为什么选择 BAGL Creator Studio</h2>
        <p class="head-desc">
          为创作者抹平技术门槛，将创意直接转化为手机端可游玩的户外实景体验
        </p>
      </div>

      <div class="bento-grid">
        <!-- 卡片 1：地理定位导航 -->
        <div class="bento-card bento-wide">
          <div class="card-text">
            <div class="card-icon"><LocationInformation /></div>
            <h3>高德地图 / 自定义手绘底图无缝支持</h3>
            <p>
              精确绑定经纬度或使用手绘园区地图坐标，支持电子围栏判定与玩家越界位置提示。
            </p>
          </div>
          <div class="card-visual visual-map">
            <div class="mock-pin">
              <el-icon><LocationFilled /></el-icon>
              <span>打卡关卡点 A</span>
            </div>
          </div>
        </div>

        <!-- 卡片 2：AR 图像识别 -->
        <div class="bento-card">
          <div class="card-text">
            <div class="card-icon"><Aim /></div>
            <h3>实景图像扫描识别</h3>
            <p>
              通过手机摄像头比对现实地标、雕塑与海报纹理，解开实体场景机关。
            </p>
          </div>
        </div>

        <!-- 卡片 3：多角色剧本 -->
        <div class="bento-card">
          <div class="card-text">
            <div class="card-icon"><UserFilled /></div>
            <h3>多角色分工与独立线索</h3>
            <p>支持为不同职业分配专属技能与私密线索，促成团队深度分工协作。</p>
          </div>
        </div>

        <!-- 卡片 4：梯度提示与积分结算 -->
        <div class="bento-card bento-wide">
          <div class="card-text">
            <div class="card-icon"><Medal /></div>
            <h3>自动化梯度提示与动态积分</h3>
            <p>
              配置超时扣分、提示惩处与通关时间系数，系统自动统计排行榜与胜负结算。
            </p>
          </div>
          <div class="card-visual visual-score">
            <div class="score-pill-show">
              <span class="sp-label">综合得分上限</span>
              <span class="sp-num">1,500 PTS</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 4. 移动端 App 所见即所得体验区 (app-download) -->
    <section class="preview-showcase-section" id="app-download">
      <div class="showcase-box">
        <div class="showcase-left">
          <span class="badge-tag">MOBILE COMPANION</span>
          <h2>手机端实景游玩 App<br />玩家一键入局探索</h2>
          <p>
            后台发布的游戏直接同步至玩家移动终端。支持离线数据包、AR
            实景相机渲染、高精度路线打卡与终局复盘。
          </p>

          <div
            class="store-buttons"
            style="display: flex; justify-content: space-around"
          >
            <a
              href="https://www.pgyer.com/RPenmbNb"
              target="_blank"
              class="download-link-card"
            >
              <el-icon class="dl-icon"><Iphone /></el-icon>
              <div class="dl-info">
                <span class="dl-sub">Download for</span>
                <span class="dl-main">Android</span>
              </div>
            </a>

            <a
              href="https://apps.apple.com/cn/app/mapgame/id6740665605"
              target="_blank"
              class="download-link-card"
            >
              <el-icon class="dl-icon"><Iphone /></el-icon>
              <div class="dl-info">
                <span class="dl-sub">Download for</span>
                <span class="dl-main">IOS</span>
              </div>
            </a>
          </div>
        </div>

        <!-- 右侧黑曜石移动端模型 -->
        <div class="showcase-right">
          <div class="mockup-phone">
            <div class="phone-top-bar"><div class="notch"></div></div>
            <div class="phone-screen">
              <div class="app-mock-nav">
                <span>实景关卡 · 城市漫游</span>
              </div>
              <div class="app-mock-hero">
                <img
                  src="./assets/img/FigmaDDSSlicePNGb26d265b3a4e9d0511811df76e1a157a.png"
                  alt=""
                  style="width: 100%; position: absolute; left: 0"
                />
                <div class="hero-floating-badge">第 3 关 / 共 6 关</div>
              </div>
              <div class="app-mock-body">
                <h4>寻找古戏台石狮印记</h4>
                <p>
                  根据指南针指引前往坐标点，找到刻有暗号的石柱并使用相机对准扫描。
                </p>
                <div class="app-mock-chip-row">
                  <span class="app-chip">难度 ★★★☆☆</span>
                  <span class="app-chip">限时 15 分钟</span>
                </div>
              </div>
              <div class="app-mock-footer">
                <button class="mock-action-btn">开启 AR 扫描</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 5. 底部页脚 (Footer) -->
    <footer class="site-footer">
      <div class="footer-container">
        <div class="footer-brand">
          <div class="brand-logo">
            <div class="logo-badge">
              <img
                src="./assets/img/img.png"
                alt=""
                style="width: 100%; height: 100%"
              />
            </div>
            <span class="main-title">BAGL Studio</span>
          </div>
          <p class="footer-motto">赋予实体空间以故事与灵魂的世界构建工具。</p>
        </div>

        <div class="footer-links-group">
          <div class="link-col">
            <span class="col-title">产品功能</span>
            <a @click="scrollToSection('features')">关卡设计器</a>
            <a @click="scrollToSection('features')">AR 识别引擎</a>
            <a @click="scrollToSection('features')">积分规则中心</a>
          </div>
          <div class="link-col">
            <span class="col-title">创作者中心</span>
            <a @click="CreateGame">发布作品</a>
            <a @click="$router.push('/list')">我的游戏库</a>
            <a @click="openAbout">开放协议</a>
          </div>
        </div>
      </div>

      <div class="footer-bottom">
        <p>© 2026 BAGL Creator Platform. All rights reserved.</p>
        <span class="tech-tag">Powered by Vue 3 & Modern SaaS Stack</span>
      </div>
    </footer>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { getToken } from "@/utils/auth";
import { ElMessageBox, ElMessage } from "element-plus";
import {
  Compass,
  Document,
  Plus,
  FolderOpened,
  Setting,
  SwitchButton,
  VideoPlay,
  ArrowDown,
  LocationInformation,
  LocationFilled,
  Aim,
  UserFilled,
  Medal,
  Iphone,
} from "@element-plus/icons-vue";

export default defineComponent({
  name: "ModernHome",
  components: {
    Compass,
    Document,
    Plus,
    FolderOpened,
    Setting,
    SwitchButton,
    VideoPlay,
    ArrowDown,
    LocationInformation,
    LocationFilled,
    Aim,
    UserFilled,
    Medal,
    Iphone,
  },
  data() {
    return {
      isLogin: getToken() !== undefined,
    };
  },
  computed: {
    userInfo(): any {
      return this.$store.state.user || {};
    },
  },
  mounted() {
    const routeParam = this.$route.params.id;
    if (routeParam === "1") {
      this.scrollToSection("features");
    } else if (routeParam === "2") {
      this.scrollToSection("app-download");
    }
  },
  methods: {
    handleCommand(command: string) {
      this.$i18n.locale = command;
      localStorage.setItem("lang", command);
      ElMessage.success({ message: "语言切换成功" });
    },
    logout() {
      this.$store
        .dispatch("user/logout")
        .then(() => {
          localStorage.clear();
          this.$router.push("/login");
        })
        .catch(() => {
          ElMessage.error("退出登录失败");
        });
    },
    CreateGame() {
      const type = this.$store.state.user?.type;

      if (type === "体验者") {
        ElMessageBox.confirm(
          "体验者无法直接创建游戏，是否立即申请成为创作者？",
          "权限提示",
          {
            confirmButtonText: "申请成为创作者",
            cancelButtonText: "取消",
            type: "warning",
          }
        )
          .then(() => {
            this.$store
              .dispatch("game/applyCreate")
              .then(() => {
                ElMessage.success("申请已提交，请等待管理员审核");
              })
              .catch(() => {});
          })
          .catch(() => {});
      } else {
        const userId = this.$store.state.user?.Id;
        this.$store
          .dispatch("game/createGame")
          .then((res: any) => {
            if (res && res.id) {
              this.$store.dispatch("game/getGameList", {
                pageNum: 1,
                pageSize: 10,
                userId: userId,
                status: 0,
              });
              this.$router.push("/edit/" + res.id + "/cover");
            }
          })
          .catch(() => {
            ElMessage.error("创建游戏失败，请稍后重试");
          });
      }
    },
    scrollToSection(elementId: string) {
      const targetElem = document.getElementById(elementId);
      if (targetElem) {
        targetElem.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    openAbout() {
      const routeData = this.$router.resolve({ path: "/about" });
      window.open(routeData.href, "_blank");
    },
  },
});
</script>

<style scoped lang="less">
// 设计系统核心色彩与视觉规范
@primary-color: #5d5fef;
@primary-hover: #4b4de0;
@primary-light: #f0f2ff;
@text-main: #0f172a;
@text-sub: #475569;
@text-muted: #94a3b8;
@bg-page: #f8fafc;
@border-color: #e2e8f0;
@glass-bg: rgba(255, 255, 255, 0.85);

.landing-page {
  width: 100%;
  min-height: 100vh;
  background-color: @bg-page;
  color: @text-main;
  position: relative;
  overflow-x: hidden;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;

  // 氛围环境光斑
  .ambient-glow {
    position: absolute;
    border-radius: 50%;
    filter: blur(120px);
    pointer-events: none;
    z-index: 0;

    &.glow-top-left {
      width: 500px;
      height: 500px;
      background: rgba(93, 95, 239, 0.12);
      top: -100px;
      left: -150px;
    }

    &.glow-top-right {
      width: 450px;
      height: 450px;
      background: rgba(14, 165, 233, 0.1);
      top: 100px;
      right: -100px;
    }

    &.glow-middle {
      width: 600px;
      height: 600px;
      background: rgba(129, 140, 248, 0.08);
      top: 900px;
      left: 20%;
    }
  }
}

// 1. 顶部毛玻璃导航条
.glass-navbar {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 68px;
  background: @glass-bg;
  backdrop-filter: blur(16px);
  border-bottom: 1px solid rgba(226, 232, 240, 0.8);
  z-index: 1000;
  display: flex;
  align-items: center;

  .nav-container {
    max-width: 1240px;
    width: 100%;
    margin: 0 auto;
    padding: 0 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .brand-logo {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;

    .logo-badge {
      width: 36px;
      height: 36px;
      border-radius: 10px;
      //background: linear-gradient(135deg, @primary-color 0%, #818cf8 100%);
      color: #ffffff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      //box-shadow: 0 4px 10px rgba(93, 95, 239, 0.3);
    }

    .brand-text {
      display: flex;
      flex-direction: column;
      line-height: 1.1;

      .main-title {
        font-size: 16px;
        font-weight: 800;
        letter-spacing: 0.5px;
        color: @text-main;
      }
      .sub-title {
        font-size: 11px;
        color: @text-muted;
        font-weight: 500;
      }
    }
  }

  .nav-links {
    display: flex;
    align-items: center;
    gap: 32px;

    .nav-item {
      font-size: 14px;
      font-weight: 600;
      color: @text-sub;
      text-decoration: none;
      cursor: pointer;
      transition: color 0.2s ease;

      &:hover {
        color: @primary-color;
      }
    }

    @media (max-width: 768px) {
      display: none;
    }
  }

  .nav-actions {
    display: flex;
    align-items: center;
    gap: 16px;

    .icon-action-btn {
      width: 38px;
      height: 38px;
      border-radius: 50%;
      border: 1px solid @border-color;
      background: #ffffff;
      color: @text-sub;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        border-color: @primary-color;
        color: @primary-color;
      }
    }

    .create-btn {
      border-radius: 8px;
      font-weight: 600;
      background: @primary-color;
      border-color: @primary-color;
      box-shadow: 0 4px 12px rgba(93, 95, 239, 0.25);
      transition: all 0.2s;

      &:hover {
        background: @primary-hover;
        border-color: @primary-hover;
        transform: translateY(-1px);
      }
    }

    .login-text-btn {
      border: none;
      background: transparent;
      font-weight: 600;
      color: @text-sub;

      &:hover {
        color: @primary-color;
      }
    }

    .join-btn {
      border-radius: 8px;
      background: @primary-color;
      border-color: @primary-color;
      font-weight: 600;

      &:hover {
        background: @primary-hover;
      }
    }

    .avatar-wrapper {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      padding: 4px 8px;
      border-radius: 20px;
      transition: background 0.2s;

      &:hover {
        background: #f1f5f9;
      }

      .user-name {
        font-size: 13px;
        font-weight: 600;
        color: @text-main;
      }
    }
  }
}

// 2. Hero 首屏
.hero-section {
  padding: 160px 24px 100px;
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: center;
  text-align: center;

  .hero-content {
    max-width: 900px;
    margin: 0 auto;
  }

  .hero-pill-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: @primary-light;
    border: 1px solid #dce2ff;
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 600;
    color: @primary-color;
    margin-bottom: 24px;

    .pulse-dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: @primary-color;
      box-shadow: 0 0 8px @primary-color;
    }
  }

  .hero-title {
    font-size: 52px;
    font-weight: 900;
    line-height: 1.25;
    letter-spacing: -1px;
    color: @text-main;
    margin-bottom: 24px;

    .gradient-text {
      background: linear-gradient(135deg, @primary-color 0%, #0ea5e9 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    @media (max-width: 768px) {
      font-size: 34px;
    }
  }

  .hero-description {
    font-size: 18px;
    line-height: 1.65;
    color: @text-sub;
    max-width: 720px;
    margin: 0 auto 36px;

    @media (max-width: 768px) {
      font-size: 15px;
    }
  }

  .hero-cta-group {
    display: flex;
    justify-content: center;
    gap: 16px;
    margin-bottom: 56px;

    .hero-primary-btn {
      height: 48px;
      padding: 0 32px;
      border-radius: 12px;
      font-size: 15px;
      font-weight: 700;
      background: @primary-color;
      border-color: @primary-color;
      box-shadow: 0 6px 20px rgba(93, 95, 239, 0.35);
      transition: all 0.25s;

      &:hover {
        background: @primary-hover;
        border-color: @primary-hover;
        transform: translateY(-2px);
        box-shadow: 0 10px 24px rgba(93, 95, 239, 0.45);
      }
    }

    .hero-secondary-btn {
      height: 48px;
      padding: 0 28px;
      border-radius: 12px;
      font-size: 15px;
      font-weight: 600;
      background: #ffffff;
      border: 1px solid @border-color;
      color: @text-main;
      transition: all 0.2s;

      &:hover {
        background: #f8fafc;
        border-color: #cbd5e1;
      }
    }
  }

  .partner-strip {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;

    .strip-label {
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 1px;
      text-transform: uppercase;
      color: @text-muted;
    }

    .partner-logos {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 12px;

      .partner-chip {
        padding: 6px 16px;
        background: #ffffff;
        border: 1px solid @border-color;
        border-radius: 20px;
        font-size: 13px;
        font-weight: 500;
        color: @text-sub;
        box-shadow: 0 2px 6px rgba(15, 23, 42, 0.03);
      }
    }
  }
}

// 3. Bento Grid 特性展示区
.features-section {
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 24px;
  position: relative;
  z-index: 1;

  .section-heading {
    text-align: center;
    margin-bottom: 50px;

    .subhead {
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 1.5px;
      color: @primary-color;
      margin-bottom: 8px;
      display: block;
    }

    .head-title {
      font-size: 32px;
      font-weight: 800;
      color: @text-main;
      margin: 0 0 12px 0;
    }

    .head-desc {
      font-size: 15px;
      color: @text-muted;
      margin: 0;
    }
  }

  .bento-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;

    @media (max-width: 900px) {
      grid-template-columns: 1fr;
    }
  }

  .bento-card {
    background: #ffffff;
    border: 1px solid @border-color;
    border-radius: 20px;
    padding: 32px;
    box-shadow: 0 4px 18px rgba(15, 23, 42, 0.03);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;

    &:hover {
      transform: translateY(-6px);
      box-shadow: 0 16px 36px -8px rgba(15, 23, 42, 0.08);
      border-color: #cbd5e1;
    }

    &.bento-wide {
      grid-column: span 2;

      @media (max-width: 900px) {
        grid-column: span 1;
      }
    }

    .card-icon {
      width: 44px;
      height: 44px;
      border-radius: 12px;
      background: @primary-light;
      color: @primary-color;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 22px;
      margin-bottom: 20px;
    }

    h3 {
      font-size: 19px;
      font-weight: 700;
      color: @text-main;
      margin: 0 0 10px 0;
    }

    p {
      font-size: 14px;
      color: @text-sub;
      line-height: 1.6;
      margin: 0;
    }

    .card-visual {
      margin-top: 24px;
      border-radius: 12px;
      height: 120px;
      background: #f8fafc;
      border: 1px dashed @border-color;
      display: flex;
      align-items: center;
      justify-content: center;

      .mock-pin {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: #ffffff;
        padding: 6px 14px;
        border-radius: 20px;
        border: 1px solid @border-color;
        color: @primary-color;
        font-weight: 600;
        font-size: 13px;
        box-shadow: 0 4px 12px rgba(93, 95, 239, 0.15);
      }

      .score-pill-show {
        display: flex;
        flex-direction: column;
        align-items: center;

        .sp-label {
          font-size: 11px;
          color: @text-muted;
        }
        .sp-num {
          font-size: 24px;
          font-weight: 900;
          color: @primary-color;
        }
      }
    }
  }
}

// 4. 手机端 App 体验区
.preview-showcase-section {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 24px 100px;
  position: relative;
  z-index: 1;

  .showcase-box {
    background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
    border: 1px solid @border-color;
    border-radius: 32px;
    padding: 60px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 40px;
    box-shadow: 0 20px 48px -12px rgba(15, 23, 42, 0.06);

    @media (max-width: 860px) {
      flex-direction: column;
      padding: 36px 24px;
    }
  }

  .showcase-left {
    max-width: 500px;

    .badge-tag {
      font-size: 11px;
      font-weight: 700;
      color: @primary-color;
      background: @primary-light;
      padding: 4px 10px;
      border-radius: 6px;
      letter-spacing: 0.5px;
      display: inline-block;
      margin-bottom: 16px;
    }

    h2 {
      font-size: 34px;
      font-weight: 800;
      color: @text-main;
      line-height: 1.3;
      margin: 0 0 18px 0;
    }

    p {
      font-size: 15px;
      color: @text-sub;
      line-height: 1.65;
      margin: 0 0 32px 0;
    }

    .download-link-card {
      display: inline-flex;
      align-items: center;
      gap: 14px;
      background: #0f172a;
      color: #ffffff;
      padding: 12px 24px;
      border-radius: 12px;
      text-decoration: none;
      transition: all 0.2s ease;
      box-shadow: 0 6px 16px rgba(15, 23, 42, 0.2);

      &:hover {
        background: #1e293b;
        transform: translateY(-2px);
      }

      .dl-icon {
        font-size: 26px;
      }

      .dl-info {
        display: flex;
        flex-direction: column;
        line-height: 1.2;

        .dl-sub {
          font-size: 11px;
          color: #94a3b8;
        }
        .dl-main {
          font-size: 14px;
          font-weight: 700;
        }
      }
    }
  }

  // 手机拟物化模型
  .showcase-right {
    .mockup-phone {
      width: 280px;
      height: 520px;
      background: #ffffff;
      border-radius: 36px;
      border: 10px solid #0f172a;
      box-shadow: 0 20px 48px -12px rgba(15, 23, 42, 0.25);
      overflow: hidden;
      display: flex;
      flex-direction: column;

      .phone-top-bar {
        height: 18px;
        display: flex;
        justify-content: center;
        background: #ffffff;

        .notch {
          width: 70px;
          height: 10px;
          background: #0f172a;
          border-bottom-left-radius: 6px;
          border-bottom-right-radius: 6px;
        }
      }

      .phone-screen {
        flex: 1;
        background: #f8fafc;
        display: flex;
        flex-direction: column;

        .app-mock-nav {
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 700;
          color: @text-main;
          border-bottom: 1px solid @border-color;
          background: #ffffff;
        }

        .app-mock-hero {
          height: 140px;
          //background: linear-gradient(135deg, #6366f1 0%, #38bdf8 100%);
          position: relative;
          display: flex;
          align-items: flex-end;
          padding: 10px;

          .hero-floating-badge {
            background: rgba(15, 23, 42, 0.7);
            backdrop-filter: blur(4px);
            color: #ffffff;
            font-size: 10px;
            font-weight: 600;
            padding: 3px 8px;
            border-radius: 4px;
          }
        }

        .app-mock-body {
          flex: 1;
          padding: 14px;
          display: flex;
          flex-direction: column;
          gap: 6px;

          h4 {
            font-size: 14px;
            font-weight: 700;
            margin: 0;
            color: @text-main;
          }

          p {
            font-size: 11.5px;
            color: @text-sub;
            line-height: 1.5;
            margin: 0;
          }

          .app-mock-chip-row {
            display: flex;
            gap: 6px;
            margin-top: 8px;

            .app-chip {
              font-size: 10px;
              background: #e2e8f0;
              color: @text-sub;
              padding: 2px 6px;
              border-radius: 4px;
            }
          }
        }

        .app-mock-footer {
          padding: 10px 14px 14px;
          background: #ffffff;
          border-top: 1px solid @border-color;

          .mock-action-btn {
            width: 100%;
            height: 34px;
            border-radius: 17px;
            background: @primary-color;
            color: #ffffff;
            border: none;
            font-size: 12px;
            font-weight: 700;
          }
        }
      }
    }
  }
}

// 5. 页脚
.site-footer {
  border-top: 1px solid @border-color;
  background: #ffffff;
  padding: 60px 24px 30px;
  position: relative;
  z-index: 1;

  .footer-container {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    gap: 40px;
    margin-bottom: 40px;

    @media (max-width: 768px) {
      flex-direction: column;
    }
  }

  .footer-brand {
    max-width: 320px;

    .brand-logo {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 12px;

      .logo-badge {
        width: 30px;
        height: 30px;
        border-radius: 8px;
        //background: @primary-color;
        color: #ffffff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
      }

      .main-title {
        font-size: 16px;
        font-weight: 800;
        color: @text-main;
      }
    }

    .footer-motto {
      font-size: 13px;
      color: @text-muted;
      line-height: 1.6;
      margin: 0;
    }
  }

  .footer-links-group {
    display: flex;
    gap: 60px;

    .link-col {
      display: flex;
      flex-direction: column;
      gap: 10px;

      .col-title {
        font-size: 13px;
        font-weight: 700;
        color: @text-main;
        margin-bottom: 4px;
      }

      a {
        font-size: 13px;
        color: @text-sub;
        text-decoration: none;
        cursor: pointer;
        transition: color 0.2s;

        &:hover {
          color: @primary-color;
        }
      }
    }
  }

  .footer-bottom {
    max-width: 1200px;
    margin: 0 auto;
    padding-top: 24px;
    border-top: 1px solid #f1f5f9;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
    color: @text-muted;

    @media (max-width: 640px) {
      flex-direction: column;
      gap: 10px;
    }
  }
}
</style>
